import { EnrollmentStatus, InvoiceStatus } from "@pilatestime/shared-types";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchInvoicesQueryOptions } from "@/api/billing";
import { fetchOrganizationEnrollmentsQueryOptions } from "@/api/enrollments";
import { fetchOrgMgmtCustomerRiskAnalysisQueryOptions } from "@/api/orgMgmtCustomerRiskAnalysis";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { cn } from "@/utils/utils";
import type { DashboardWidgetProps } from "./MetricCards";
import { DashboardCard, WidgetEmpty, WidgetError } from "./parts";

const TOP_N = 5;
const TOP_RISK = 3;
const RISK_WINDOW_DAYS = 30;
const RISK_MIN_CANCELLATIONS = 2;
const DAY_MS = 24 * 60 * 60 * 1000;

const enrollmentsSearch = {
	page: 1,
	limit: 20,
	search: "",
	expiringSoon: true,
	statuses: [EnrollmentStatus.ACTIVE],
} as const;

const billingSearch = { page: 1, limit: 20, search: "" } as const;

/**
 * Query options for the two "needs attention" lists, exported so the route
 * loader prefetches with the EXACT same keys the widget's `useQuery` reads —
 * the prefetch warms the cache and the render is a cache hit (no double fetch,
 * N+1 gate §8).
 */
export const expiringEnrollmentsQueryOptions = (
	userId: string,
	token: string,
	orgId: string,
) =>
	fetchOrganizationEnrollmentsQueryOptions(
		userId,
		token,
		orgId,
		{ page: 1, limit: TOP_N },
		"",
		{ expiringSoon: true, statuses: [EnrollmentStatus.ACTIVE] },
	);

export const overdueInvoicesQueryOptions = (
	userId: string,
	token: string,
	orgId: string,
) =>
	fetchInvoicesQueryOptions(userId, token, orgId, {
		page: 1,
		limit: TOP_N,
		status: InvoiceStatus.OVERDUE,
	});

/**
 * Top at-risk customers for the widget — same defaults as the Customer Risk
 * Analysis page (30-day window, ≥2 cancellations) so the query key matches and
 * the prefetch is a cache hit. Gated behind `VIEW_CUSTOMER_RISK_ANALYSIS`; the
 * backend already sorts worst-attendance first, so the widget just takes the
 * first `TOP_RISK`.
 */
export const customersAtRiskQueryOptions = (orgId: string, token: string) =>
	fetchOrgMgmtCustomerRiskAnalysisQueryOptions(
		orgId,
		token,
		RISK_WINDOW_DAYS,
		RISK_MIN_CANCELLATIONS,
	);

function AttentionRow({
	dotClass,
	title,
	detail,
	action,
}: {
	dotClass: string;
	title: string;
	detail: string;
	action: React.ReactNode;
}) {
	return (
		<div className="flex items-center gap-3 py-2.5">
			<span
				className={cn("h-2 w-2 shrink-0 rounded-full", dotClass)}
				aria-hidden="true"
			/>
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium text-base-content">
					{title}
				</p>
				<p className="truncate text-xs text-base-content/50">{detail}</p>
			</div>
			{action}
		</div>
	);
}

const actionLinkClass =
	"shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-primary transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

/**
 * A labeled sub-section inside the widget (e.g. "Overdue invoices",
 * "At-risk customers"). Renders its own header so the distinct attention
 * groups read as separate lists rather than one flat stream. Collapses to
 * nothing when empty and not errored, so no lonely header shows.
 */
function AttentionGroup({
	label,
	isError,
	isEmpty,
	onRetry,
	children,
}: {
	label: string;
	isError: boolean;
	isEmpty: boolean;
	onRetry: () => void;
	children: React.ReactNode;
}) {
	if (!isError && isEmpty) {
		return null;
	}
	return (
		<section>
			<p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-base-content/40">
				{label}
			</p>
			{isError ? (
				<div className="px-1 py-2">
					<WidgetError onRetry={onRetry} />
				</div>
			) : (
				<div className="divide-y divide-base-300 px-1">{children}</div>
			)}
		</section>
	);
}

/**
 * "Needs attention" widget (plan §7.5): expiring enrollments + overdue invoices,
 * top-5 each, with "view all" deep-links. Each list is its own query so a single
 * failing endpoint degrades only its section (graceful degradation §6).
 */
/**
 * `canViewRisk` is injected by the parent (which owns the access profile) rather
 * than read here — it keeps this widget decoupled from the router/permission
 * context and independently testable. Defaults to `false` so the at-risk query
 * never fires for callers that don't grant the permission.
 */
export function NeedsAttention({
	orgId,
	userId,
	token,
	canViewRisk = false,
}: DashboardWidgetProps & { canViewRisk?: boolean }) {
	const { t } = useTranslation();
	const { locale } = useDateFormatter();

	const expiringQuery = useQuery(
		expiringEnrollmentsQueryOptions(userId, token, orgId),
	);

	const overdueQuery = useQuery(
		overdueInvoicesQueryOptions(userId, token, orgId),
	);

	// Disabled for roles without the permission so no forbidden call fires.
	const riskQuery = useQuery({
		...customersAtRiskQueryOptions(orgId, token),
		enabled: canViewRisk,
	});

	const expiring = expiringQuery.data?.enrollments.slice(0, TOP_N) ?? [];
	const overdue = overdueQuery.data?.invoices.slice(0, TOP_N) ?? [];
	const atRisk = canViewRisk
		? (riskQuery.data?.customers.slice(0, TOP_RISK) ?? [])
		: [];

	const isPending =
		expiringQuery.isPending ||
		overdueQuery.isPending ||
		(canViewRisk && riskQuery.isPending);
	const allErrored =
		expiringQuery.isError &&
		overdueQuery.isError &&
		(!canViewRisk || riskQuery.isError);

	const count =
		(expiringQuery.data?.pagination.total ?? 0) +
		(overdueQuery.data?.pagination.total ?? 0) +
		(canViewRisk ? (riskQuery.data?.customers.length ?? 0) : 0);

	const formatAttendanceRate = (
		scheduledCount: number,
		cancellationCount: number,
	): number => {
		if (scheduledCount <= 0) {
			return 0;
		}
		const attended = Math.max(0, scheduledCount - cancellationCount);
		return Math.round((attended / scheduledCount) * 100);
	};

	const formatDaysUntil = (endDate?: Date | number | string): string => {
		if (endDate == null) {
			return t("dashboard.home.needsAttention.expiresSoon");
		}
		const days = Math.max(
			0,
			Math.ceil((new Date(endDate).getTime() - Date.now()) / DAY_MS),
		);
		return t("dashboard.home.needsAttention.expiresInDays", { count: days });
	};

	const formatCurrency = (amount: string, currency: string): string => {
		const value = Number.parseFloat(amount);
		if (Number.isNaN(value)) {
			return amount;
		}
		return new Intl.NumberFormat(locale, {
			style: "currency",
			currency: currency || "BRL",
		}).format(value);
	};

	const renderBody = () => {
		if (isPending) {
			return (
				<div className="animate-pulse space-y-3 py-2">
					{[0, 1, 2].map((i) => (
						<div key={i} className="flex items-center gap-3">
							<div className="h-2 w-2 rounded-full bg-base-300" />
							<div className="h-4 flex-1 rounded bg-base-300" />
						</div>
					))}
				</div>
			);
		}
		if (allErrored) {
			return (
				<WidgetError
					onRetry={() => {
						expiringQuery.refetch();
						overdueQuery.refetch();
						if (canViewRisk) {
							riskQuery.refetch();
						}
					}}
				/>
			);
		}
		const nothingToShow =
			expiring.length === 0 &&
			overdue.length === 0 &&
			atRisk.length === 0 &&
			!expiringQuery.isError &&
			!overdueQuery.isError &&
			(!canViewRisk || !riskQuery.isError);
		if (nothingToShow) {
			return (
				<WidgetEmpty
					icon={<CheckCircle2 className="h-8 w-8 opacity-50" />}
					message={t("dashboard.home.needsAttention.allCaughtUp")}
				/>
			);
		}
		return (
			<div className="space-y-4">
				<AttentionGroup
					label={t("dashboard.home.needsAttention.groupEnrollments")}
					isError={expiringQuery.isError}
					isEmpty={expiring.length === 0}
					onRetry={() => expiringQuery.refetch()}
				>
					{expiring.map((enrollment) => (
						<AttentionRow
							key={enrollment.id}
							dotClass="bg-warning-content"
							title={`${enrollment.customer.firstName} ${enrollment.customer.lastName}`.trim()}
							detail={formatDaysUntil(enrollment.endDate)}
							action={
								<Link
									to="/app/org/$orgId/enrollments"
									params={{ orgId }}
									search={enrollmentsSearch}
									className={actionLinkClass}
								>
									{t("dashboard.home.needsAttention.renew")}
								</Link>
							}
						/>
					))}
				</AttentionGroup>

				<AttentionGroup
					label={t("dashboard.home.needsAttention.groupInvoices")}
					isError={overdueQuery.isError}
					isEmpty={overdue.length === 0}
					onRetry={() => overdueQuery.refetch()}
				>
					{overdue.map((invoice) => {
						const customerName = invoice.customer
							? `${invoice.customer.firstName} ${invoice.customer.lastName}`.trim()
							: "";
						const detail = [
							formatCurrency(invoice.totalAmount, invoice.currency),
							customerName,
						]
							.filter(Boolean)
							.join(" · ");
						return (
							<AttentionRow
								key={invoice.id}
								dotClass="bg-error-content"
								title={t("dashboard.home.needsAttention.invoiceOverdue", {
									number: invoice.invoiceNumber ?? invoice.id.slice(0, 8),
								})}
								detail={detail}
								action={
									<Link
										to="/app/org/$orgId/billing"
										params={{ orgId }}
										search={billingSearch}
										className={actionLinkClass}
									>
										{t("dashboard.home.needsAttention.collect")}
									</Link>
								}
							/>
						);
					})}
				</AttentionGroup>

				{canViewRisk && (
					<AttentionGroup
						label={t("dashboard.home.needsAttention.groupCustomerRisk")}
						isError={riskQuery.isError}
						isEmpty={atRisk.length === 0}
						onRetry={() => riskQuery.refetch()}
					>
						{atRisk.map((customer) => (
							<AttentionRow
								key={customer.customerId}
								dotClass="bg-error-content"
								title={customer.customerFullName}
								detail={t("dashboard.home.needsAttention.atRiskDetail", {
									count: customer.cancellationCount,
									rate: formatAttendanceRate(
										customer.scheduledCount,
										customer.cancellationCount,
									),
								})}
								action={
									<Link
										to="/app/org/$orgId/org-management/customer-risk-analysis"
										params={{ orgId }}
										className={actionLinkClass}
									>
										{t("dashboard.home.needsAttention.review")}
									</Link>
								}
							/>
						))}
					</AttentionGroup>
				)}
			</div>
		);
	};

	return (
		<DashboardCard className="p-4">
			<div className="mb-2 flex items-center gap-2 px-1">
				<h3 className="text-base font-bold text-base-content">
					{t("dashboard.home.needsAttention.title")}
				</h3>
				{count > 0 && (
					<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1.5 text-xs font-semibold text-error-content">
						{count}
					</span>
				)}
			</div>

			{renderBody()}

			{(expiring.length > 0 || overdue.length > 0) && (
				<div className="mt-2 flex justify-end gap-4 px-1 pt-1">
					<Link
						to="/app/org/$orgId/enrollments"
						params={{ orgId }}
						search={enrollmentsSearch}
						className="text-xs font-semibold text-primary hover:underline"
					>
						{t("common.viewAll")}
					</Link>
				</div>
			)}
		</DashboardCard>
	);
}
